import { useCallback, useMemo } from 'react';
import { Editor, useEditorState } from '@tiptap/react';
import type { Level } from '@tiptap/extension-heading';

import { pt } from '@flintmint/web-shared/utils/ui';

const headingSizeMap = {
  1: 19,
  2: 18,
  3: 17,
  4: 18,
  5: 17,
  6: 16,
} as const;

export const useFormattingCommands = (editor: Editor) => {
  const setHeading = useCallback((level: Level) => editor.chain().focus().setHeading({ level }).run(), [editor]);

  const commands = useMemo(
    () => ({
      onBold: () => editor.chain().focus().toggleBold().run(),
      onItalic: () => editor.chain().focus().toggleItalic().run(),
      onStrike: () => editor.chain().focus().toggleStrike().run(),
      onUnderline: () => editor.chain().focus().toggleUnderline().run(),
      onCode: () => editor.chain().focus().toggleCode().run(),
      onHorizontalRule: () => editor.chain().focus().setHorizontalRule().run(),
      onParagraph: () => editor.chain().focus().unsetAllMarks().liftListItem('listItem').setParagraph().run(),
      onUndo: () => editor.commands.undo(),
      onRedo: () => editor.commands.redo(),
      onHeading1: () => setHeading(1),
      onHeading2: () => setHeading(2),
      onHeading3: () => setHeading(3),
      onHeading4: () => setHeading(4),
      onHeading5: () => setHeading(5),
      onHeading6: () => setHeading(6),
      onAlignLeft: () => editor.chain().focus().setTextAlign('left').run(),
      onAlignCenter: () => editor.chain().focus().setTextAlign('center').run(),
      onAlignRight: () => editor.chain().focus().setTextAlign('right').run(),
      onJustify: () => editor.chain().focus().setTextAlign('justify').run(),
      onSetFontSize: (fontSize: string) => {
        if (!fontSize) return editor.chain().focus().unsetFontSize().run();
        return editor.chain().focus().setFontSize(pt(fontSize)).run();
      },
      onBulletList: () => editor.chain().focus().toggleBulletList().run(),
      onOrderedList: () => editor.chain().focus().toggleOrderedList().run(),
    }),
    [editor, setHeading]
  );

  const states = useEditorState({
    editor,
    selector: ({ editor }) => {
      const attrs = editor.getAttributes('textStyle');
      const activeHeadingLevel = ([1, 2, 3, 4, 5, 6] as const).find((lvl) => editor.isActive('heading', { level: lvl })) ?? null;

      const headingFontSize = activeHeadingLevel ? pt(headingSizeMap[activeHeadingLevel]) : pt(14);

      return {
        isCode: editor.isActive('code'),
        isBold: editor.isActive('bold'),
        isItalic: editor.isActive('italic'),
        isStrike: editor.isActive('strike'),
        isUnderline: editor.isActive('underline'),
        isAlignLeft: editor.isActive({ textAlign: 'left' }),
        isAlignRight: editor.isActive({ textAlign: 'right' }),
        isAlignCenter: editor.isActive({ textAlign: 'center' }),
        isJustified: editor.isActive({ textAlign: 'justify' }),

        isParagraph: editor.isActive('paragraph') && !editor.isActive('orderedList') && !editor.isActive('bulletList'),
        isBulletList: editor.isActive('bulletList'),
        isOrderedList: editor.isActive('orderedList'),
        isHorizontalRule: editor.isActive('horizontalRule'),

        isH1: activeHeadingLevel === 1,
        isH2: activeHeadingLevel === 2,
        isH3: activeHeadingLevel === 3,
        isH4: activeHeadingLevel === 4,
        isH5: activeHeadingLevel === 5,
        isH6: activeHeadingLevel === 6,

        currentSize: attrs?.fontSize || headingFontSize,

        canUndo: canExecuteEditorCommand(editor, 'undo'),
        canRedo: canExecuteEditorCommand(editor, 'redo'),
      };
    },
  });

  return { commands, states };
};

const canExecuteEditorCommand = (editor: Editor, command: 'undo' | 'redo') => {
  try {
    // Unit tests revealed TipTap can briefly expose a state where editor.can() throws (Cannot read properties of null (reading 'can')). The guard prevents that crash and keeps toolbar state computation stable
    return editor.can()[command]();
  } catch {
    return false;
  }
};
